import type { ChipId, GameweekDetail } from '../types'

/** Just the slice of the viewer's chip state this needs. `AppView['chips']` satisfies it
 *  structurally, and taking the narrow shape keeps this module free of the app-wide view. */
export interface ViewerChipGameweeks {
  doubleUpUsedGameweeks: number[]
  cleanSweepUsedGameweeks: number[]
  goalfestUsedGameweeks: number[]
}

/**
 * Which chips to badge this gameweek with — a claim about *whose* chips they were, so it has two
 * sources depending on whose week is open.
 *
 * Another player's detail is assembled from their picks and scored with their chip, so the detail
 * itself is the authority; the viewer's ledger says nothing about a stranger's gameweek. A branch
 * returning nothing here shows a chip-inflated total on a week
 * that looked chip-free.
 *
 * Your own detail comes from stored `seasonDetails`, some of it written before the field existed,
 * so the live ledger stays the authority there.
 */
export function resolveActiveChips(
  hasOwner: boolean,
  detail: GameweekDetail | null,
  chips: ViewerChipGameweeks,
  gameweek: number
): ChipId[] {
  if (hasOwner) return detail?.activeChips ?? []
  return (
    [
      chips.doubleUpUsedGameweeks.includes(gameweek) && 'doubleUp',
      chips.cleanSweepUsedGameweeks.includes(gameweek) && 'cleanSweep',
      chips.goalfestUsedGameweeks.includes(gameweek) && 'goalfest',
    ] as const
  ).filter((id): id is ChipId => id !== false)
}
